import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Check, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import DateField from "@/components/DateField";
import { api } from "@/lib/api";
import { fmtMoney } from "@/lib/promo";
import { track } from "@/lib/analytics";
import { SERVICES, COMING_SOON_SLUGS, CONTACT } from "@/lib/content";
import { toast } from "sonner";
import { BOOKING_CONFIRMATION_KEY } from "@/pages/BookingConfirmed";

// Service-page titles → pricing keys (for real slot availability when the
// modal opens from a Services page instead of the calculator)
const TITLE_TO_KEY = {
  "Ceramic Coating": "ceramic-5yr",
  "Paint Correction": "paint-correction-1step",
  "Interior Detailing": "interior-detail",
  "Exterior Detailing": "exterior-detail",
  "Exterior Decontamination Detail": "exterior-detail",
  "Bed Liner": "bed-liner",
  "Headlight Restoration": "headlight-restoration",
  "Odor Treatment": "odor-removal",
};

export default function QuoteModal({ open, onOpenChange, prefill = {} }) {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    vehicle: "",
    service: "",
    message: "",
    estimatedPrice: null,
    estimatePayload: null,
    booking: null,
    quote: null,
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  // In-modal slot picker — the client chooses day & time at confirmation
  const [bkDate, setBkDate] = useState("");
  const [bkTime, setBkTime] = useState("");
  const [bkSlots, setBkSlots] = useState(null); // {closed, duration_min, slots[]}
  const [bkSlotsLoading, setBkSlotsLoading] = useState(false);
  const [horizonDays, setHorizonDays] = useState(30);
  const [minBookDate, setMinBookDate] = useState(null);

  useEffect(() => {
    if (open) {
      setForm((f) => ({ ...f, ...prefill }));
      setDone(false);
      setError("");
      setBkDate(prefill?.booking?.date || "");
      setBkTime(prefill?.booking?.time || "");
      setBkSlots(null);
      api.get("/pricing/config")
        .then(({ data }) => {
          setHorizonDays(data?.booking?.horizon_days || 30);
          setMinBookDate(data?.booking?.min_date || null);
        })
        .catch(() => {});
    }
  }, [open, prefill]);

  // service keys for slot availability: calculator booking payload, or the
  // service chosen in the select (Services-page flow)
  const slotKeys = form.booking?.service_keys?.length
    ? form.booking.service_keys
    : form.booking?.service_key
      ? [form.booking.service_key]
      : (TITLE_TO_KEY[form.service] ? [TITLE_TO_KEY[form.service]] : []);
  const canPickSlot = slotKeys.length > 0;

  // fetch real availability when a date is picked inside the modal
  useEffect(() => {
    if (!bkDate || slotKeys.length === 0) { setBkSlots(null); return; }
    let cancelled = false;
    setBkSlotsLoading(true);
    api.get("/booking/slots", { params: { date: bkDate, service_keys: slotKeys.join(",") } })
      .then(({ data }) => { if (!cancelled) setBkSlots(data); })
      .catch(() => { if (!cancelled) setBkSlots(null); })
      .finally(() => !cancelled && setBkSlotsLoading(false));
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bkDate, form.booking, form.service]);

  useEffect(() => {
    if (!open) return;
    const onEsc = (e) => e.key === "Escape" && onOpenChange(false);
    document.addEventListener("keydown", onEsc);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onEsc);
      document.body.style.overflow = "";
    };
  }, [open, onOpenChange]);

  const setF = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    const b = form.booking;
    const isBooking = !!(bkDate && bkTime && (b?.service_key || b?.service_keys?.length));
    try {
      if (isBooking) {
        const { data } = await api.post("/booking/book", {
          name: form.name,
          phone: form.phone,
          email: form.email,
          message: form.message,
          year: b.year,
          make: b.make,
          model: b.model,
          service_key: b.service_key || undefined,
          service_keys: b.service_keys || undefined,
          service_option: b.service_option || undefined,
          service_options: b.service_options || undefined,
          addon_selections: b.addon_selections || {},
          addon_flags: b.addon_flags || [],
          bundles: b.bundles || undefined,
          date: bkDate,
          time: bkTime,
        });
        // Backend confirmed the booking — hand the details to the dedicated
        // confirmation page (Google Ads "Book Appointment" conversion URL).
        // On any error we stay in the modal; no redirect happens.
        const confirmation = {
          ref: data.appointment_id,
          service_name: data.service_name,
          date: data.date,
          time: data.time,
          duration_min: data.duration_min,
          total: data.total,
          price_type: data.price_type,
          vehicle: form.quote?.vehicle
            ? `${form.quote.vehicle.year} ${form.quote.vehicle.make} ${form.quote.vehicle.model}`
            : form.vehicle,
          services: form.quote?.services || null,
          addons: (form.quote?.addons || []).filter((a) => !a.key.startsWith("promo_")),
          savings: form.quote?.discounts?.total_discount || 0,
        };
        try { sessionStorage.setItem(BOOKING_CONFIRMATION_KEY, JSON.stringify(confirmation)); } catch { /* storage blocked — router state still works */ }
        track("booking_completed", {
          booked_with_slot: true,
          primary_service: b?.service_keys?.[0] || b?.service_key || null,
          services_count: b?.service_keys?.length || (b?.service_key ? 1 : 0),
          bundles: (b?.bundles || []).join(","),
          total: form.quote?.total ?? null,
        });
        toast.success("Booked! We'll confirm your appointment shortly.");
        onOpenChange(false);
        navigate("/booking-confirmed", { state: confirmation });
        return;
      } else {
        // Services-page flow: attach the preferred slot + mapped service key so
        // the CRM lead shows what the client wants and when (no vehicle → no
        // pricing snapshot; staff confirm the time manually).
        const mapped = TITLE_TO_KEY[form.service];
        const payload = { ...form };
        if (mapped && bkDate) {
          payload.booking = { service_keys: [mapped], date: bkDate, time: bkTime || null };
        }
        await api.post("/quotes", payload);
        setDone(true);
        toast.success("Quote request sent — we'll be in touch shortly.");
      }
      track("booking_completed", {
        booked_with_slot: isBooking,
        primary_service: b?.service_keys?.[0] || b?.service_key || form.service || null,
        services_count: b?.service_keys?.length || (b?.service_key ? 1 : 0),
        bundles: (b?.bundles || []).join(","),
        total: form.quote?.total ?? null,
      });
      setForm({
        name: "",
        phone: "",
        email: "",
        vehicle: "",
        service: "",
        message: "",
        estimatedPrice: null,
        estimatePayload: null,
        booking: null,
        quote: null,
      });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg =
        typeof detail === "string"
          ? detail
          : Array.isArray(detail)
          ? detail.map((d) => d?.msg).filter(Boolean).join(" ")
          : "Something went wrong. Please try again.";
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="quote-modal"
          className="fixed inset-0 z-[100] flex items-end md:items-center justify-center p-0 md:p-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <div
            className="absolute inset-0 bg-black/70 backdrop-blur-md"
            onClick={() => onOpenChange(false)}
          />
          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 40, scale: 0.98 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="relative w-full md:max-w-2xl max-h-[92dvh] flex flex-col bg-[#121216] border border-white/12 md:rounded-3xl rounded-t-3xl overflow-hidden shadow-[0_20px_80px_rgba(0,0,0,0.6)]"
          >
            <div className="shrink-0 flex items-center justify-between px-6 md:px-10 py-6 border-b border-white/12">
              <div>
                <div className="font-mono-alt text-[11px] text-[#1F5EFF]">
                  · Free Quote ·
                </div>
                <h3 className="mt-2 font-display text-2xl md:text-3xl font-light text-white">
                  Tell us about your vehicle.
                </h3>
              </div>
              <button
                data-testid="quote-modal-close"
                onClick={() => onOpenChange(false)}
                className="w-10 h-10 rounded-full border border-white/15 text-white/70 hover:text-white hover:bg-white/5 flex items-center justify-center transition-colors"
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {done ? (
              <div className="p-10 md:p-14 text-center overflow-y-auto flex-1" data-testid="quote-success">
                <div className="mx-auto w-16 h-16 rounded-full bg-[#1F5EFF]/15 border border-[#1F5EFF]/40 flex items-center justify-center mb-6">
                  <Check className="w-7 h-7 text-[#1F5EFF]" />
                </div>
                <div className="font-display text-2xl md:text-3xl text-white">
                  Request received.
                </div>
                <p className="mt-3 text-white/60">
                  Our team will reach out within 24 hours with a personalized quote.
                </p>
                <button
                  onClick={() => onOpenChange(false)}
                  className="mt-8 inline-flex items-center px-6 py-3 rounded-full border border-white/20 text-white hover:bg-white/5"
                >
                  Close
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="p-6 md:p-10 space-y-4 overflow-y-auto flex-1" data-testid="quote-form">
                {form.quote && (
                  <div className="rounded-2xl border border-[#1F5EFF]/30 bg-[#1F5EFF]/5 p-4 mb-2" data-testid="quote-booking-summary">
                    <div className="font-mono-alt text-[10px] text-[#8db2ff] uppercase tracking-widest mb-2">Your calculated quote</div>
                    {form.quote.services?.length > 0 ? (
                      <div className="space-y-1">
                        {form.quote.services.map((s, i) => (
                          <div key={i}>
                            <div className="flex justify-between text-sm">
                              <span className="text-white font-medium">{s.name}{s.option ? ` — ${s.option.label}` : ""}</span>
                              <span className="text-white/70 tabular-nums">{s.price != null ? fmtMoney(s.price) : "Custom"}</span>
                            </div>
                            {s.discount && (
                              <div className="flex justify-between text-xs mt-0.5" data-testid={`quote-summary-discount-${i}`}>
                                <span className="text-emerald-300/80 pl-3">
                                  {s.discount.type === "grand_opening_main"
                                    ? `Grand Opening −${s.discount.percent}%`
                                    : `Additional service −${s.discount.percent}%`}
                                </span>
                                <span className="text-emerald-300 tabular-nums">−{fmtMoney(s.discount.amount)}</span>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-white text-sm font-medium">{form.quote.service_name}</div>
                    )}
                    <div className="text-white/50 text-xs mt-1">{form.quote.vehicle.year} {form.quote.vehicle.make} {form.quote.vehicle.model}</div>
                    {form.quote.addons.filter((a) => !a.key.startsWith("promo_")).map((a, i) => (
                      <div key={i} className="flex justify-between text-xs mt-1">
                        <span className={a.price < 0 ? "text-emerald-300/80 pl-3" : "text-white/60"}>{a.label}</span>
                        <span className={`tabular-nums ${a.price < 0 ? "text-emerald-300" : "text-white/80"}`}>
                          {a.custom ? "Custom" : a.price < 0 ? `−${fmtMoney(Math.abs(a.price))}` : `+${fmtMoney(a.price)}`}
                        </span>
                      </div>
                    ))}
                    {form.quote.discounts?.total_discount > 0 && (
                      <div className="flex justify-between text-xs mt-2" data-testid="quote-summary-savings">
                        <span className="text-emerald-300 font-medium">You save</span>
                        <span className="text-emerald-300 font-medium tabular-nums">−{fmtMoney(form.quote.discounts.total_discount)}</span>
                      </div>
                    )}
                    <div className="flex justify-between mt-2 pt-2 border-t border-white/10">
                      <span className="text-white/70 text-sm">Estimated total</span>
                      <span className="text-white font-semibold tabular-nums" data-testid="quote-summary-total">
                        {form.quote.price_type === "custom" ? "Custom Quote"
                          : form.quote.price_type === "starting" ? `from ${fmtMoney(form.quote.total)}` : fmtMoney(form.quote.total)}
                      </span>
                    </div>
                  </div>
                )}
                {canPickSlot && (
                  <div className="rounded-2xl border border-white/12 bg-white/5 p-4 mb-2" data-testid="quote-slot-picker">
                    <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                      <span className="font-mono-alt text-[10px] text-white/50 uppercase tracking-widest">Preferred date & time</span>
                      {bkDate && bkTime && (
                        <span className="text-emerald-300 text-xs font-medium" data-testid="quote-slot">
                          Reserved: {bkDate} at {bkTime}
                        </span>
                      )}
                    </div>
                    <DateField
                      value={bkDate}
                      min={(() => { const t = new Date().toLocaleDateString("en-CA"); return minBookDate && minBookDate > t ? minBookDate : t; })()}
                      max={new Date(Date.now() + horizonDays * 864e5).toLocaleDateString("en-CA")}
                      onChange={(v) => { setBkDate(v); setBkTime(""); }}
                      className="input"
                      testid="quote-date"
                      placeholder="Pick a date"
                    />
                    {bkDate && (
                      bkSlotsLoading ? (
                        <div className="mt-3 text-white/40 text-xs flex items-center gap-2">
                          <span className="w-3.5 h-3.5 border-2 border-white/20 border-t-white/70 rounded-full animate-spin" />
                          Checking availability…
                        </div>
                      ) : bkSlots?.closed ? (
                        <div className="mt-3 text-amber-300/80 text-xs" data-testid="quote-closed">
                          {bkSlots?.reason === "before_opening"
                            ? `We open ${new Date(bkSlots.opens + "T12:00:00").toLocaleDateString("en-CA", { month: "long", day: "numeric" })} — online booking starts that day.`
                            : bkDate && new Date(bkDate + "T12:00:00").getDay() === 0
                              ? `Sundays are by appointment only — call ${CONTACT.phone} and we'll book you in.`
                              : "We're closed on this day — please pick another date."}
                        </div>
                      ) : bkSlots ? (
                        <div className="mt-3 grid grid-cols-4 sm:grid-cols-5 gap-1.5" data-testid="quote-slots">
                          {bkSlots.slots.map((s) => (
                            <button key={s.time} type="button" disabled={!s.available}
                              onClick={() => setBkTime(bkTime === s.time ? "" : s.time)}
                              data-testid={`quote-slot-${s.time}`}
                              className={`px-2 py-2 rounded-lg border text-xs tabular-nums transition-colors ${
                                bkTime === s.time
                                  ? "bg-[#1F5EFF] border-[#1F5EFF] text-white"
                                  : s.available
                                    ? "border-white/10 text-white/80 hover:border-white/30 hover:text-white"
                                    : "border-white/5 text-white/25 line-through cursor-not-allowed"}`}>
                              {s.time}
                            </button>
                          ))}
                        </div>
                      ) : null
                    )}
                    <div className="mt-2 text-[11px] text-white/35 leading-relaxed">
                      {form.booking
                        ? "Optional: pick a date and time to reserve a real slot — or leave empty and we'll arrange it with you."
                        : "Optional: choose your preferred day & time — we'll confirm availability by phone or email."}
                    </div>
                  </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Field label="Full Name" required>
                    <input
                      required
                      value={form.name}
                      onChange={setF("name")}
                      data-testid="quote-input-name"
                      className="input"
                      placeholder="John Smith"
                    />
                  </Field>
                  <Field label="Phone" required>
                    <input
                      required
                      value={form.phone}
                      onChange={setF("phone")}
                      data-testid="quote-input-phone"
                      className="input"
                      placeholder="(368) 380 8483"
                    />
                  </Field>
                </div>
                <Field label="Email" required>
                  <input
                    required
                    type="email"
                    value={form.email}
                    onChange={setF("email")}
                    data-testid="quote-input-email"
                    className="input"
                    placeholder="you@email.com"
                  />
                </Field>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Field label="Vehicle (Make · Model · Year)">
                    <input
                      value={form.vehicle}
                      onChange={setF("vehicle")}
                      data-testid="quote-input-vehicle"
                      className="input"
                      placeholder="Porsche 911 · 2024"
                    />
                  </Field>
                  <Field label="Service">
                    <select
                      value={form.service}
                      onChange={setF("service")}
                      data-testid="quote-input-service"
                      className="input"
                    >
                      <option value="">Select…</option>
                      {SERVICES.map((s) => (
                        <option key={s.slug} value={s.title}>
                          {COMING_SOON_SLUGS.includes(s.slug) ? `${s.title} — Coming Soon` : s.title}
                        </option>
                      ))}
                      {form.service && !SERVICES.some((s) => s.title === form.service) && (
                        <option value={form.service}>{form.service}</option>
                      )}
                      <option value="Booking / Consultation">
                        Booking / Consultation
                      </option>
                    </select>
                  </Field>
                </div>
                <Field label="Message">
                  <textarea
                    rows={4}
                    value={form.message}
                    onChange={setF("message")}
                    data-testid="quote-input-message"
                    className="input resize-none"
                    placeholder="Anything else we should know?"
                  />
                </Field>

                {error && (
                  <div className="text-sm text-red-400" data-testid="quote-error">
                    {error}
                  </div>
                )}

                <div className="pt-3 flex items-center justify-between gap-3 flex-wrap">
                  <div className="text-xs text-white/40 font-mono-alt">
                    We reply within 24h · No spam
                  </div>
                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="quote-submit"
                    className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-[#1F5EFF] text-white font-semibold hover:bg-white hover:text-black transition-colors disabled:opacity-60"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Sending…
                      </>
                    ) : form.booking?.date && form.booking?.time ? (
                      "Confirm Booking"
                    ) : (
                      "Submit Request"
                    )}
                  </button>
                </div>
              </form>
            )}
          </motion.div>

          {/* Inline styles for input class */}
          <style>{`
            .input {
              width: 100%;
              background: #17171c;
              border: 1px solid rgba(255,255,255,0.10);
              color: #fff;
              padding: 12px 14px;
              border-radius: 12px;
              font-size: 14px;
              transition: border-color 0.2s, background 0.2s;
              outline: none;
            }
            .input::placeholder { color: rgba(255,255,255,0.35); }
            .input:focus {
              border-color: #1F5EFF;
              background: #0e1220;
            }
            select.input { appearance: none; background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%23ffffff88' stroke-width='2'><polyline points='6 9 12 15 18 9'/></svg>"); background-repeat: no-repeat; background-position: right 14px center; padding-right: 40px; }
            select.input option { background: #17171c; color: #fff; }
          `}</style>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <div className="font-mono-alt text-[10px] text-white/50 mb-2">
        {label} {required && <span className="text-[#1F5EFF]">*</span>}
      </div>
      {children}
    </label>
  );
}
